import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Spinner } from "@/components/Kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Eye, Palette, Volume2, Gamepad2, KeyRound } from "lucide-react";

const VIS = [["everyone", "Everyone"], ["friends", "Friends Only"], ["nobody", "Nobody"]];
const TROPHY = [["public", "Public"], ["friends", "Friends Only"], ["private", "Private"]];
const REQ = [["all", "Allow All"], ["friends", "Friends Only"], ["fof", "Friends of Friends"], ["block", "Block All"]];
const ANIM = [["high", "High"], ["medium", "Medium"], ["low", "Low (static)"]];
const TEXT = [["small", "Small"], ["normal", "Normal"], ["large", "Large"]];
const TZ = ["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Asia/Kolkata", "Asia/Tokyo", "Australia/Sydney"];

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-border/50 last:border-0">
      <div><div className="text-sm font-semibold">{label}</div>{hint && <div className="text-xs text-muted-foreground">{hint}</div>}</div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
function Pick({ value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 rounded-[var(--radius)] bg-muted border border-border mono text-xs focus:outline-none focus:border-primary">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

export default function SettingsModal({ open, onClose, user, refreshUser }) {
  const { setTheme } = useTheme();
  const [p, setP] = useState(user?.prefs || {});
  const [pets, setPets] = useState([]);
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setP(user?.prefs || {}); api.get("/pets").then((r) => setPets(r.data.pets)).catch(() => {}); } }, [open, user]);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/prefs", { prefs: p });
      if (data.prefs.theme) setTheme(data.prefs.theme);
      toast.success("Preferences saved.");
      await refreshUser?.(); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const savePin = async () => {
    if (!(pin.length === 4 && /^\d{4}$/.test(pin))) { toast.error("PIN must be exactly 4 digits."); return; }
    try { await api.post("/auth/pin/set", { pin }); toast.success("PIN saved — never share it."); setPin(""); await refreshUser?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="settings-modal">
        <DialogHeader><DialogTitle className="font-head">Preferences Terminal</DialogTitle>
          <DialogDescription className="text-xs">Tune your account, privacy, visuals, audio and gameplay defaults.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="account" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-5 shrink-0">
            <TabsTrigger value="account" data-testid="tab-account"><ShieldCheck className="w-4 h-4" /></TabsTrigger>
            <TabsTrigger value="privacy" data-testid="tab-privacy"><Eye className="w-4 h-4" /></TabsTrigger>
            <TabsTrigger value="visuals" data-testid="tab-visuals"><Palette className="w-4 h-4" /></TabsTrigger>
            <TabsTrigger value="audio" data-testid="tab-audio"><Volume2 className="w-4 h-4" /></TabsTrigger>
            <TabsTrigger value="gameplay" data-testid="tab-gameplay"><Gamepad2 className="w-4 h-4" /></TabsTrigger>
          </TabsList>
          <div className="overflow-y-auto flex-1 mt-3 pr-1">
            <TabsContent value="account">
              <div className="label-cap mb-1">Account & Security</div>
              <Row label="Newsletters" hint="Station bulletins"><Switch checked={!!p.email_news} onCheckedChange={(v) => set("email_news", v)} data-testid="pref-news" /></Row>
              <Row label="Product updates"><Switch checked={!!p.email_updates} onCheckedChange={(v) => set("email_updates", v)} /></Row>
              <Row label="Event invites"><Switch checked={!!p.email_events} onCheckedChange={(v) => set("email_events", v)} /></Row>
              <div className="panel p-3 mt-4">
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><KeyRound className="w-4 h-4 text-primary" /> 4-Digit PIN {user?.pin_set && <span className="label-cap text-[9px] text-secondary">SET</span>}</div>
                <p className="text-xs text-muted-foreground mb-2">A kid-friendly second factor. This is your PIN — never share it.</p>
                <div className="flex gap-2">
                  <input data-testid="pin-input" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••" className="w-24 px-3 py-2 rounded-[var(--radius)] bg-muted border border-border mono text-lg tracking-[0.4em] text-center focus:outline-none focus:border-primary" />
                  <button onClick={savePin} data-testid="save-pin-btn" className="btn-secondary text-xs">{user?.pin_set ? "Update PIN" : "Set PIN"}</button>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="privacy">
              <div className="label-cap mb-1">Privacy & Social</div>
              <Row label="Profile visibility"><Pick testid="pref-profile-vis" value={p.profile_visibility} onChange={(v) => set("profile_visibility", v)} options={VIS} /></Row>
              <Row label="Trophy case"><Pick value={p.trophy_visibility} onChange={(v) => set("trophy_visibility", v)} options={TROPHY} /></Row>
              <Row label="Friend requests"><Pick testid="pref-friend-req" value={p.friend_requests} onChange={(v) => set("friend_requests", v)} options={REQ} /></Row>
              <Row label="Direct messages"><Pick value={p.dms} onChange={(v) => set("dms", v)} options={REQ} /></Row>
            </TabsContent>
            <TabsContent value="visuals">
              <div className="label-cap mb-1">Visuals & Accessibility</div>
              <Row label="Theme" hint="Station white / obsidian neon"><Pick testid="pref-theme" value={p.theme} onChange={(v) => set("theme", v)} options={[["dark", "Dark"], ["light", "Light"]]} /></Row>
              <Row label="Animation quality"><Pick value={p.animation} onChange={(v) => set("animation", v)} options={ANIM} /></Row>
              <Row label="Text size"><Pick value={p.text_size} onChange={(v) => set("text_size", v)} options={TEXT} /></Row>
            </TabsContent>
            <TabsContent value="audio">
              <div className="label-cap mb-1">Audio & SFX</div>
              <Row label="Master volume" hint={`${p.master_volume ?? 80}%`}>
                <div className="w-40"><Slider value={[p.master_volume ?? 80]} min={0} max={100} step={5} onValueChange={([v]) => set("master_volume", v)} data-testid="pref-volume" /></div>
              </Row>
              <Row label="Ambient music"><Switch checked={!!p.music} onCheckedChange={(v) => set("music", v)} /></Row>
              <Row label="Sound effects"><Switch checked={!!p.sfx} onCheckedChange={(v) => set("sfx", v)} /></Row>
            </TabsContent>
            <TabsContent value="gameplay">
              <div className="label-cap mb-1">Gameplay Defaults</div>
              <Row label="Default robot">
                <Pick value={p.default_pet || ""} onChange={(v) => set("default_pet", v || null)} options={[["", "— none —"], ...pets.map((pt) => [pt.pet_id, pt.name])]} />
              </Row>
              <Row label="Time zone" hint="Used for resets & leaderboards">
                <Pick testid="pref-tz" value={p.timezone} onChange={(v) => set("timezone", v)} options={TZ.map((z) => [z, z])} />
              </Row>
            </TabsContent>
          </div>
        </Tabs>
        <div className="flex justify-end gap-2 pt-3 border-t border-border shrink-0">
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="btn-primary text-sm">{saving ? <Spinner className="w-4 h-4" /> : "Save Preferences"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
